import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

interface GiftCategoryCardProps {
    category: {
        seqId: number;
        categoryCode: string;
        categoryName: string;
        categoryDesc: string;
        useYn: string;
        crtDate?: string;
    };
    onClick: (category: any) => void;
    onEdit?: (category: any) => void;
    onDelete?: () => void;
    darkMode?: boolean;
}

function GiftCategoryCard({ category, onClick, onEdit, onDelete, darkMode }: GiftCategoryCardProps) {
    const getStatusColor = (useYn: string) => {
        return useYn === "Y" ? "success" : "error";
    };

    const getStatusLabel = (useYn: string) => {
        return useYn === "Y" ? "사용중" : "미사용";
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s ease-in-out",
                cursor: "pointer",
                border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
                boxShadow: darkMode
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.08)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: darkMode
                        ? "0 8px 20px rgba(255, 255, 255, 0.15)"
                        : "0 8px 20px rgba(0, 0, 0, 0.15)",
                    borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
                },
            }}
            onClick={() => onClick(category)}
        >
            <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <MDTypography variant="h5" fontWeight="bold">
                        {category.categoryName}
                    </MDTypography>
                    <Chip
                        label={getStatusLabel(category.useYn)}
                        color={getStatusColor(category.useYn)}
                        size="small"
                        sx={{
                            fontWeight: "medium",
                            ...(category.useYn === "Y" && {
                                backgroundColor: "#4CAF50",
                                color: "#ffffff",
                                "& .MuiChip-label": {
                                    color: "#ffffff",
                                },
                            }),
                        }}
                    />
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <MDBox>
                    <MDBox display="flex" alignItems="center" mb={1.5}>
                        <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}>
                            description
                        </Icon>
                        <MDTypography variant="body2" color="text" sx={{
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            textOverflow: 'ellipsis'
                        }}>
                            {category.categoryDesc || "-"}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                        <MDBox display="flex" alignItems="center">
                            {category.crtDate && (
                                <>
                                    <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}>
                                        schedule
                                    </Icon>
                                    <MDTypography variant="caption" color="text.secondary">
                                        {new Date(category.crtDate).toLocaleDateString()}
                                    </MDTypography>
                                </>
                            )}
                        </MDBox>

                        {(onEdit || onDelete) && (
                            <MDBox display="flex" gap={0.5}>
                                {onEdit && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(category);
                                        }}
                                        sx={{
                                            color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            "&:hover": {
                                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                color: "#49a3f1",
                                            },
                                        }}
                                    >
                                        <Icon fontSize="small">edit</Icon>
                                    </IconButton>
                                )}
                                {onDelete && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete();
                                        }}
                                        sx={{
                                            color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            "&:hover": {
                                                backgroundColor: darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                                color: "#f44336",
                                            },
                                        }}
                                    >
                                        <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                )}
                            </MDBox>
                        )}
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
}

export default GiftCategoryCard;
